import LockBlocked from "@mui/icons-material/Block";
import LockUnknown from "@mui/icons-material/HelpOutline";
import InfoIcon from "@mui/icons-material/Info";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockBreakin from "@mui/icons-material/NewReleases";
import {
	Alert,
	Avatar,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	LinearProgress,
	List,
	ListItem,
	ListItemSecondaryAction,
	ListItemText,
	Stack,
	SxProps,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import {green, grey, orange, red} from "@mui/material/colors";
import {ConfirmDialog, ErrorAlert, useFlag} from "@variocube/app-ui";
import React, {ReactElement, useCallback, useMemo, useRef, useState} from "react";
import {useAsync, useAsyncCallback} from "react-async-hook";
import {LocationVerification} from "../../component/location-verification";
import {useBooking} from "../../context/booking";
import {type BookingBoxes, BookingEventType, useBookingClient, useBookingEvent} from "../../data/bookings";
import {B2Occupancy} from "../../data/client.gen";
import {BoxAvailability, CubeAvailability, useSiteApi} from "../../data/sites";
import {useCloseOnBack} from "../../history-utils";
import {useLocalization} from "../../i18n";
import {notBlank} from "../../utils";

/**
 * Whether a cube has at least one free box of a type this booking needs, i.e. selecting it would occupy at least one
 * more box. We can't compute the exact per-type remainder on the client (an occupancy carries no box type), so the
 * backend's /occupy decides how many boxes each attempt actually fills; this is only a soft gate that marks a cube as
 * non-selectable when it cannot contribute at all. Cubes are always rendered — never hidden — so a customer standing at
 * the site sees every locker bank they can physically see.
 */
export function cubeCanHelp(bookedTypes: string[], availability: BoxAvailability | undefined) {
	if (!availability) return false;
	return bookedTypes.some(type => (availability[type] ?? 0) > 0);
}

/**
 * The booking's box demand: how many boxes it needs in total, and which sizes those are.
 *
 * A size booked with quantity 0 is not booked at all and must not count as a booked type (#1265): it would let
 * {@link cubeCanHelp} accept a locker bank whose only free boxes are of a size this booking does not want, and it
 * would show up in the picker's per-cube availability line. Bookings edited before the backend started dropping zero
 * quantities still carry such entries, so filtering here also repairs them.
 */
export function bookedBoxDemand(boxes: BookingBoxes | undefined) {
	const booked = Object.entries(boxes ?? {}).filter(([, quantity]) => quantity > 0);
	return {
		totalBoxes: booked.reduce((sum, [, quantity]) => sum + quantity, 0),
		bookedTypes: booked.map(([type]) => type),
	};
}

// How long a successful location verification vouches for the customer's presence at a cube. After this window a
// re-open verifies again — a verification from minutes ago is no proof the customer is still standing at the locker.
const CUBE_VERIFICATION_TTL_MS = 60_000;

interface OccupancyState {
	occupancies: B2Occupancy[];
	setOccupancies: React.Dispatch<React.SetStateAction<B2Occupancy[]>>;
}

/**
 * Keeps the local occupancy list in sync with live lock-status events for the booking. Shared by the standard and
 * simplified booking views so both reflect Open/Closed changes without a reload.
 */
export function useOccupancyEvents({occupancies, setOccupancies}: OccupancyState) {
	const {booking} = useBooking();
	if (booking !== undefined) {
		const client = useBookingClient(booking.id);
		useBookingEvent(client, BookingEventType.B2OccupancyEvent, event => {
			setOccupancies(prev =>
				prev.map(o => {
					if (o.uuid === event.uuid) {
						o.lockStatus = event.lockStatus;
						o.openedTimes = event.openedTimes;
					}
					return o;
				})
			);
		});
	}
}

/**
 * Encapsulates the "offer another locker bank" fallback shared by the standard and simplified booking views: loading
 * every cube of the site, tracking which cubes were already tried this session (for labeling, not filtering), and
 * occupying a customer-selected cube.
 */
export function useCubeSelection({occupancies, setOccupancies}: OccupancyState) {
	const {booking, occupy} = useBooking();
	const {getCubeAvailabilities} = useSiteApi();

	const [selecting, setSelecting] = useState(false);
	const [cubeAvailabilities, setCubeAvailabilities] = useState<CubeAvailability[]>([]);
	// Cubes already attempted this session — labeled (not hidden) so the customer never loses sight of a bank.
	const [triedCubeIds, setTriedCubeIds] = useState<string[]>([]);

	const {loading: cubesLoading, error: cubesError, execute: loadCubeAvailabilities} = useAsyncCallback(
		getCubeAvailabilities,
	);
	const {loading: occupyingCube, error: occupyCubeError, execute: runOccupy} = useAsyncCallback(occupy);

	// Seed the tried list with the cube auto-occupy already attempted so it renders as "no longer available".
	function markTried(cubeId: string) {
		setTriedCubeIds(prev => (prev.includes(cubeId) ? prev : [...prev, cubeId]));
	}

	function openCubeSelection() {
		if (booking === undefined) return;
		loadCubeAvailabilities(booking.id)
			.then(cubes => {
				setCubeAvailabilities(cubes);
				setSelecting(true);
			})
			.catch(() => {
				// Surfaced to the customer via the cubesError alert; stay on the current screen.
			});
	}

	function cancelCubeSelection() {
		setSelecting(false);
	}

	function selectCube(cubeId: string) {
		runOccupy(cubeId)
			.then(result => {
				setOccupancies(result ?? []);
				markTried(cubeId);
				setSelecting(false);
			})
			.catch(console.error);
	}

	return {
		selecting,
		cubeAvailabilities,
		triedCubeIds,
		cubesLoading,
		cubesError,
		occupyingCube,
		occupyCubeError,
		markTried,
		openCubeSelection,
		cancelCubeSelection,
		selectCube,
	};
}

interface AutoOccupyOptions extends OccupancyState {
	// The booking's activity flag (see {@code isBookingActive}) — passed in so both views gate the auto-occupy on the
	// exact same notion of "active" instead of each recomputing it.
	active: boolean;
	// Invoked with the cube that was auto-occupied so callers can mark it as tried (renders it "no longer available"
	// in the locker-bank picker instead of a still-selectable button).
	onOccupied: (cubeId: string) => void;
}

/**
 * The one auto-occupy-on-load decision, shared by the standard and simplified booking views so they cannot drift.
 * Fires a single {@code occupy(autoOccupyCubeId)} when the booking is active, never had an occupancy, wasn't released,
 * and the server resolved a cube — at most once per mounted booking. The {@code attempted} guard makes it idempotent
 * against StrictMode double-mounts and refreshes mid round-trip (the backend dedupe is per-JVM and time-bounded), and
 * keeping {@code occupancies} out of the dependency array stops the attempt from re-firing on every lock-status event.
 */
export function useAutoOccupy({active, occupancies, setOccupancies, onOccupied}: AutoOccupyOptions) {
	const {booking, occupy} = useBooking();

	const autoOccupyCubeId = booking?.autoOccupyCubeId;
	const hasReleased = (booking?.endedOccupancies ?? []).length > 0;
	const attempted = useRef(false);

	const {loading: autoOccupying, error: autoOccupyError} = useAsync(async () => {
		if (
			booking !== undefined
			&& active
			&& occupancies.length === 0
			&& !hasReleased
			&& notBlank(autoOccupyCubeId)
			&& !attempted.current
		) {
			attempted.current = true;
			const result = await occupy(autoOccupyCubeId as string);
			setOccupancies(result ?? []);
			onOccupied(autoOccupyCubeId as string);
		}
	}, [booking?.id, active]);

	return {autoOccupying, autoOccupyError};
}

/**
 * The booking's locker card on the (standard) booking view. On load it auto-occupies whenever that is necessary
 * (paid, active, never had an occupancy) and possible (the server resolved an {@code autoOccupyCubeId}); no extra tap.
 * On success it shows the occupied box(es) with an open button; when the cube is full or no cube is known it offers
 * the site's other locker banks in a dialog. Opening a box is gated by location verification (also a dialog) against
 * that box's own cube. Released lockers stay visible as released and are only re-occupied on an explicit tap — never
 * automatically, since the customer just gave them up.
 */
export function BookingOccupancy() {
	const {t} = useLocalization();
	const {booking, isBookingActive, changeBox, open, release} = useBooking();

	const [occupancies, setOccupancies] = useState<B2Occupancy[]>(booking?.occupancies ?? []);
	useOccupancyEvents({occupancies, setOccupancies});

	const cubeSelection = useCubeSelection({occupancies, setOccupancies});

	// Open flow: opening a box requires location verification against that box's cube. We cache the last verified cube
	// so opening several boxes on the same bank only verifies once — but only for a short window (see
	// {@link CUBE_VERIFICATION_TTL_MS}), so a re-open long after the customer walked away verifies again.
	const [verifiedCube, setVerifiedCube] = useState<{ cubeId: string; expiresAt: number }>();
	const [pendingOpen, setPendingOpen] = useState<B2Occupancy>();
	const [confirmOpen, setConfirmOpen] = useState<B2Occupancy>();
	const [verifyingOpen, setVerifyingOpen] = useState(false);
	const [releasePrompt, setReleasePrompt, clearReleasePrompt] = useFlag(false);
	// Change flow (#997): swap to another free same-size box in the same cube, only before the box was ever opened.
	const [changeOccupancy, setChangeOccupancy] = useState<B2Occupancy>();

	// The back button closes the confirmation dialogs instead of leaving the booking (the verification and
	// locker-bank dialogs wire this up themselves).
	useCloseOnBack(confirmOpen !== undefined, () => setConfirmOpen(undefined));
	useCloseOnBack(releasePrompt, clearReleasePrompt);
	useCloseOnBack(changeOccupancy !== undefined, () => setChangeOccupancy(undefined));

	const autoOccupyCubeId = booking?.autoOccupyCubeId;
	// Boxes the customer released earlier. Their presence means the lockers were given up deliberately, so the view
	// shows them as released and never auto-occupies again — occupying anew takes an explicit tap.
	const endedOccupancies = booking?.endedOccupancies ?? [];
	const hasReleased = endedOccupancies.length > 0;

	const {totalBoxes, bookedTypes} = useMemo(
		() => bookedBoxDemand(booking?.boxes as BookingBoxes | undefined),
		[booking],
	);
	const isFulfilled = totalBoxes > 0 && occupancies.length >= totalBoxes;

	// Auto-occupy on mount, via the shared hook so the standard and simplified views stay in lock-step.
	const {autoOccupying, autoOccupyError} = useAutoOccupy({
		active: Boolean(isBookingActive),
		occupancies,
		setOccupancies,
		onOccupied: cubeSelection.markTried,
	});

	const occupying = autoOccupying || cubeSelection.occupyingCube;

	const canRelease = booking !== undefined && occupancies.length > 0 && booking.site.enableRelease;

	// "Open and release" is only offered from the second access on — on the first open the customer is storing
	// their things ("ask on access" semantics; the keycard app gates the same way on openedTimes).
	const wasOpenedOnce = occupancies.some(o => (o.openedTimes ?? 0) > 0);

	// The site's opening precaution decides what stands between the open tap and the box actually opening:
	// location verification (QR or typed code) plus the confirm dialog, the confirm dialog alone (with a
	// presence checkbox and the box number displayed big), or nothing at all.
	const openingPrecaution = booking?.site.openingPrecaution;

	// Whether the box's cube was location-verified recently enough to skip re-verification (evaluated lazily on the
	// open tap so the TTL is checked against the moment the customer actually opens).
	function isCubeVerified(cubeId: string | undefined) {
		return verifiedCube !== undefined
			&& notBlank(cubeId)
			&& verifiedCube.cubeId === cubeId
			&& Date.now() < verifiedCube.expiresAt;
	}

	function requestOpen(occupancy: B2Occupancy) {
		if (openingPrecaution === "None") {
			void open(occupancy);
		}
		else if (openingPrecaution === "CustomerConfirmation" || isCubeVerified(occupancy.cubeId)) {
			setConfirmOpen(occupancy);
		}
		else {
			setPendingOpen(occupancy);
			setVerifyingOpen(true);
		}
	}

	function handleOpenVerified(cubeId: string) {
		setVerifiedCube({cubeId, expiresAt: Date.now() + CUBE_VERIFICATION_TTL_MS});
		setVerifyingOpen(false);
		if (pendingOpen !== undefined && pendingOpen.cubeId === cubeId) {
			setConfirmOpen(pendingOpen);
		}
		setPendingOpen(undefined);
	}

	// Confirmed change-box: the backend occupies a same-size replacement first, then releases the old box; swap the
	// row optimistically so the card shows the new number right away.
	const handleConfirmChange = useCallback(async () => {
		if (changeOccupancy?.uuid !== undefined) {
			const previousUuid = changeOccupancy.uuid;
			const updated = await changeBox(changeOccupancy);
			if (updated !== undefined) {
				setOccupancies(prev => prev.map(o => o.uuid === previousUuid ? updated : o));
			}
			setChangeOccupancy(undefined);
		}
	}, [changeOccupancy, changeBox]);

	// Releasing ends every occupancy on the server; clear the local list too so the card doesn't keep showing the
	// released box (the booking itself stays active — the customer can pick a locker bank again if they change
	// their mind). Dismiss the confirmation dialog explicitly: it used to close as a side effect of release()'s
	// refresh() unmounting the whole view, but the view now stays mounted across refreshes (see context/booking.tsx),
	// so without this the dialog would linger with its back-button history marker un-consumed (#1187).
	const handleRelease = useCallback(async () => {
		await release();
		setOccupancies([]);
		clearReleasePrompt();
	}, [release, clearReleasePrompt]);

	const handleOpen = useCallback(async (releaseAfter: boolean) => {
		if (confirmOpen !== undefined) {
			await open(confirmOpen);
			setConfirmOpen(undefined);
			if (releaseAfter) {
				await handleRelease();
			}
		}
	}, [confirmOpen, open, handleRelease]);

	if (booking === undefined) return null;

	return (
		<Card>
			<CardHeader title={t(totalBoxes > 1 ? "booking.locker.titlePlural" : "booking.locker.title")} />
			<CardContent>
				{
					/* All occupancy states render inside one min-height area sized for the booking's box count, so the
				    transition from "preparing" to the locker number(s) (or the locker-bank fallback) does not shift the
				    page. Over-reserving slightly is fine — the content is centered in the leftover space. */
				}
				<Stack
					direction="column"
					alignItems="center"
					justifyContent="center"
					gap={2}
					minHeight={240 + Math.max(0, totalBoxes - 1) * 200}
				>
					{occupying && (
						<>
							<CircularProgress />
							<Typography textAlign="center">
								{t(totalBoxes > 1 ? "booking.locker.preparingPlural" : "booking.locker.preparing")}
							</Typography>
						</>
					)}

					{!occupying && occupancies.length > 0 && (
						<Stack direction="column" alignSelf="stretch" gap={2}>
							{occupancies.map((occupancy, index) => (
								<React.Fragment key={occupancy.uuid}>
									{index > 0 && <Divider />}
									<OccupancyResult
										occupancy={occupancy}
										onOpen={requestOpen}
										onChange={setChangeOccupancy}
										canChange={Boolean(isBookingActive) && Boolean(occupancy.changeable)
											&& occupancy.lockStatus === "Closed"
											&& (occupancy.openedTimes ?? 0) === 0}
									/>
								</React.Fragment>
							))}
						</Stack>
					)}

					{!occupying && hasReleased && !isFulfilled && (
						<Stack direction="column" alignItems="center" gap={2}>
							{endedOccupancies.map(occupancy => (
								<ReleasedBox key={occupancy.uuid} occupancy={occupancy} />
							))}
						</Stack>
					)}

					{!occupying && !isFulfilled && (
						hasReleased && notBlank(autoOccupyCubeId)
							? (
								<>
									<Typography textAlign="center">
										{t(
											totalBoxes > 1
												? "booking.locker.releasedMessagePlural"
												: "booking.locker.releasedMessage",
										)}
									</Typography>
									<Button
										variant="contained"
										color="primary"
										disableElevation
										disabled={!isBookingActive}
										onClick={() => cubeSelection.selectCube(autoOccupyCubeId as string)}
									>
										{t(totalBoxes > 1 ? "booking.locker.occupyPlural" : "booking.locker.occupy")}
									</Button>
									{booking.siteHasMultipleCubes && (
										<Button
											disabled={!isBookingActive}
											onClick={cubeSelection.openCubeSelection}
										>
											{t("booking.autoOccupy.selectLockerbank")}
										</Button>
									)}
								</>
							)
							: booking.siteHasMultipleCubes
							? (
								<>
									<InfoIcon fontSize="large" style={{color: orange[500]}} />
									<Typography textAlign="center">
										{occupancies.length > 0 || notBlank(autoOccupyCubeId)
											? t("booking.autoOccupy.anotherLockerBank")
											: t("booking.autoOccupy.chooseLockerbank")}
									</Typography>
									<Button
										variant="contained"
										color="primary"
										disableElevation
										disabled={!isBookingActive}
										onClick={cubeSelection.openCubeSelection}
									>
										{t("booking.autoOccupy.selectLockerbank")}
									</Button>
								</>
							)
							: (
								// Single-cube site whose only bank can't (fully) serve the booking: there is no other
								// locker bank to offer, so point the customer at support instead of a dead-end picker.
								<>
									<InfoIcon fontSize="large" style={{color: orange[500]}} />
									<Typography textAlign="center">
										{t("booking.occupy.insufficientLockers")}
									</Typography>
									{notBlank(booking.site.contactPhone) && (
										<Typography>
											<strong>{t("sites.phoneNumber")}</strong>:{" "}
											<a href={"tel:" + booking.site.contactPhone}>{booking.site.contactPhone}</a>
										</Typography>
									)}
								</>
							)
					)}

					{autoOccupyError && (
						<>
							<InfoIcon fontSize="large" style={{color: red[500]}} />
							<Typography textAlign="center">{t("booking.occupy.error")}</Typography>
						</>
					)}

					{cubeSelection.occupyCubeError && !cubeSelection.selecting && (
						<ErrorAlert error={cubeSelection.occupyCubeError} />
					)}
					{cubeSelection.cubesError && <ErrorAlert error={cubeSelection.cubesError} />}
					{cubeSelection.cubesLoading && <LinearProgress sx={{alignSelf: "stretch"}} />}
				</Stack>
			</CardContent>

			{
				/* The release offer sits flush on the card's bottom edge, spanning its full width. On release-enabled
			    sites the wrapper always reserves the alert's height, so it popping in after auto-occupy completes
			    doesn't shift the page. */
			}
			{booking.site.enableRelease && (
				<Box minHeight={64} display="flex" flexDirection="column" justifyContent="flex-end">
					{canRelease && (
						<Alert
							severity="warning"
							sx={{borderRadius: 0}}
							action={
								<Button
									variant="contained"
									color="secondary"
									size="small"
									disableElevation
									onClick={setReleasePrompt}
								>
									{t("booking.release.button")}
								</Button>
							}
						>
							<Typography>{t("booking.release.message")}</Typography>
						</Alert>
					)}
				</Box>
			)}

			{/* Location verification for opening — verifies against the box's own cube. */}
			<LocationVerifierDialog
				open={verifyingOpen}
				requiredCubeId={pendingOpen?.cubeId}
				cubeDescription={pendingOpen?.cubeDescription}
				onVerified={handleOpenVerified}
				onCancel={() => {
					setVerifyingOpen(false);
					setPendingOpen(undefined);
				}}
			/>

			{/* Cube selection — offered when the auto-occupy cube is full or unknown. */}
			<CubeSelectionDialog bookedTypes={bookedTypes} selection={cubeSelection} />

			<ConfirmDialog
				open={changeOccupancy !== undefined}
				onClose={() => setChangeOccupancy(undefined)}
				title={t("boxes.change.confirmationTitle")}
				cancel={t("actions.cancel")}
				onConfirm={handleConfirmChange}
			>
				<Typography>{t("boxes.change.confirmationMessage")}</Typography>
			</ConfirmDialog>

			<ConfirmDialog
				open={releasePrompt}
				onClose={clearReleasePrompt}
				title={t("booking.release.button")}
				cancel={t("actions.cancel")}
				onConfirm={handleRelease}
				color="warning"
			>
				<Typography gutterBottom>{t("booking.release.message")}</Typography>
				<Alert severity="warning">
					<Typography>{t("booking.release.warning")}</Typography>
				</Alert>
			</ConfirmDialog>

			<Dialog fullWidth maxWidth="xs" open={confirmOpen !== undefined}>
				{confirmOpen !== undefined && (
					<OpenConfirmation
						occupancy={confirmOpen}
						requirePresenceConfirmation={openingPrecaution === "CustomerConfirmation"}
						offerRelease={canRelease && wasOpenedOnce && booking.site.askReleaseLocker}
						onOpen={handleOpen}
						onCancel={() => setConfirmOpen(undefined)}
					/>
				)}
			</Dialog>
		</Card>
	);
}

interface OpenConfirmationProps {
	occupancy: B2Occupancy;
	/** Customer-confirmation mode: show the box number big and gate the open buttons on a presence checkbox. */
	requirePresenceConfirmation: boolean;
	offerRelease: boolean;
	onOpen: (releaseAfter: boolean) => void;
	onCancel: () => void;
}

/**
 * Content of the open-confirmation dialog. Mounted only while the dialog is open, so the presence checkbox starts
 * unchecked on every attempt.
 */
function OpenConfirmation(
	{occupancy, requirePresenceConfirmation, offerRelease, onOpen, onCancel}: OpenConfirmationProps,
) {
	const {t} = useLocalization();
	const [presenceConfirmed, setPresenceConfirmed] = useState(false);
	const canOpen = !requirePresenceConfirmation || presenceConfirmed;
	return (
		<>
			<DialogTitle>{t("boxes.open.confirmationTitle")}</DialogTitle>
			<DialogContent>
				{requirePresenceConfirmation
					? (
						<Stack direction="column" alignItems="center" gap={1}>
							<Typography variant="h2" lineHeight={1}>
								{t("boxes.short").toUpperCase()} {occupancy.boxNumber}
							</Typography>
							{notBlank(occupancy.cubeDescription) && (
								<Typography variant="subtitle2" color="text.secondary">
									{occupancy.cubeDescription}
								</Typography>
							)}
							<FormControlLabel
								control={
									<Checkbox
										checked={presenceConfirmed}
										onChange={ev => setPresenceConfirmed(ev.target.checked)}
										color="primary"
									/>
								}
								label={t("boxes.open.presenceConfirmation")}
							/>
						</Stack>
					)
					: <Typography gutterBottom>{t("boxes.open.confirmationMessage")}</Typography>}
				<Stack direction="column" gap={1} mt={2}>
					<Button
						disableElevation
						variant="contained"
						color="primary"
						disabled={!canOpen}
						onClick={() => onOpen(false)}
					>
						{t("boxes.open.openAction")}
					</Button>
					{offerRelease && (
						<Button
							disableElevation
							variant="contained"
							color="primary"
							disabled={!canOpen}
							onClick={() => onOpen(true)}
						>
							{t("boxes.open.openAndReleaseAction")}
						</Button>
					)}
					<Button onClick={onCancel}>{t("actions.cancel")}</Button>
				</Stack>
			</DialogContent>
		</>
	);
}

interface OccupancyResultProps {
	occupancy: B2Occupancy;
	onOpen: (o: B2Occupancy) => void;
	onChange: (o: B2Occupancy) => void;
	/** Whether the change-box offer (#997) applies: active booking, backend-changeable, closed, never opened. */
	canChange: boolean;
}

/**
 * Single occupied box, optimized for the common single-box booking: the box number large, its cube name small, and an
 * open button below. While the box is still closed and was never opened, a secondary "Change" action offers swapping
 * to another free same-size box in the same cube.
 */
function OccupancyResult({occupancy, onOpen, onChange, canChange}: OccupancyResultProps) {
	const {t} = useLocalization();
	const {lockStatus, boxNumber, cubeDescription} = occupancy;

	let sxProps: SxProps = {};
	let icon: ReactElement;
	switch (lockStatus ?? "") {
		case "Open":
			sxProps = {background: orange[400]};
			icon = <LockOpenIcon color="inherit" />;
			break;
		case "Closed":
			sxProps = {background: green[400]};
			icon = <LockIcon color="inherit" />;
			break;
		case "Breakin":
			sxProps = {background: red[400]};
			icon = <LockBreakin color="inherit" />;
			break;
		case "Blocked":
			sxProps = {background: grey[400]};
			icon = <LockBlocked color="inherit" />;
			break;
		default:
			icon = <LockUnknown color="inherit" />;
			break;
	}

	return (
		<Stack direction="column" alignItems="center" gap={1}>
			<Avatar sx={{...sxProps, color: "white"}}>{icon}</Avatar>
			<Typography variant="h2" lineHeight={1}>
				{t("boxes.short").toUpperCase()} {boxNumber}
			</Typography>
			{notBlank(cubeDescription) && (
				<Typography variant="subtitle2" color="text.secondary">{cubeDescription}</Typography>
			)}
			<Button
				variant="contained"
				disableElevation
				color="primary"
				size="large"
				onClick={() => onOpen(occupancy)}
			>
				{t("boxes.open.button")}
			</Button>
			{canChange && (
				<Button
					variant="outlined"
					disableElevation
					color="primary"
					size="small"
					onClick={() => onChange(occupancy)}
				>
					{t("boxes.change.button")}
				</Button>
			)}
		</Stack>
	);
}

interface CubeSelectionProps {
	bookedTypes: string[];
	cubes: CubeAvailability[];
	triedCubeIds: string[];
	onSelect: (cubeId: string) => void;
	onCancel: () => void;
}

/**
 * Lists every locker bank of the site so the customer can pick one when the auto-occupy cube is full or unknown. Cubes
 * that cannot contribute are shown disabled with an explanatory label rather than hidden — a customer standing at the
 * site can see every bank, so hiding one is confusing.
 */
export function CubeSelection({bookedTypes, cubes, triedCubeIds, onSelect, onCancel}: CubeSelectionProps) {
	const {t} = useLocalization();
	const {booking} = useBooking();

	// No listed cube can contribute (includes a single-cube site whose only cube is full). Surface the support contact.
	const noneSelectable = cubes.every(cube =>
		!cubeCanHelp(bookedTypes, cube.availability) || triedCubeIds.includes(cube.cubeId)
	);

	return (
		<Stack direction="column" alignItems="center" gap={2} px={2} py={1}>
			<InfoIcon fontSize="large" style={{color: orange[500]}} />
			<Typography textAlign="center">{t("booking.cubeSelection.message")}</Typography>
			<List sx={{width: "100%"}}>
				{cubes.map(cube => {
					const hasCapacity = cubeCanHelp(bookedTypes, cube.availability);
					const alreadyTried = triedCubeIds.includes(cube.cubeId);
					const selectable = hasCapacity && !alreadyTried;
					const label = selectable
						? t("booking.cubeSelection.select")
						: alreadyTried
						? t("booking.cubeSelection.alreadyTried")
						: t("booking.cubeSelection.fullyBooked");
					return (
						<ListItem key={cube.cubeId} divider>
							<ListItemText
								primary={notBlank(cube.cubeDescription) ? cube.cubeDescription : cube.cubeId}
								secondary={bookedTypes
									.map(type => `${type}: ${(cube.availability ?? {})[type] ?? 0}`)
									.join("  ")}
							/>
							<ListItemSecondaryAction>
								<Button
									variant="contained"
									color="primary"
									size="small"
									disableElevation
									disabled={!selectable}
									onClick={() => onSelect(cube.cubeId)}
								>
									{label}
								</Button>
							</ListItemSecondaryAction>
						</ListItem>
					);
				})}
			</List>
			{noneSelectable && (
				<>
					<Typography textAlign="center">{t("booking.occupy.insufficientLockers")}</Typography>
					{notBlank(booking?.site.contactPhone) && (
						<Typography>
							<strong>{t("sites.phoneNumber")}</strong>:{" "}
							<a href={"tel:" + booking?.site.contactPhone}>{booking?.site.contactPhone}</a>
						</Typography>
					)}
				</>
			)}
			<Button variant="contained" color="inherit" disableElevation onClick={onCancel}>
				{t("actions.cancel")}
			</Button>
		</Stack>
	);
}

interface ReleasedBoxProps {
	occupancy: B2Occupancy;
}

/** A box the customer released earlier — kept visible in a muted style so they still know which locker they had. */
function ReleasedBox({occupancy}: ReleasedBoxProps) {
	const {t} = useLocalization();
	const {boxNumber, cubeDescription} = occupancy;
	return (
		<Stack direction="column" alignItems="center" gap={1}>
			<Typography variant="h4" lineHeight={1} color="text.disabled" sx={{textDecoration: "line-through"}}>
				{t("boxes.short").toUpperCase()} {boxNumber}
			</Typography>
			<Chip size="small" label={t("booking.locker.released")} />
			{notBlank(cubeDescription) && (
				<Typography variant="subtitle2" color="text.secondary">{cubeDescription}</Typography>
			)}
		</Stack>
	);
}

/** Dialogs below take over the whole screen on phones — the customer is standing at the locker, give them room. */
function useMobileFullScreen() {
	const theme = useTheme();
	return useMediaQuery(theme.breakpoints.down("sm"));
}

interface CubeSelectionDialogProps {
	bookedTypes: string[];
	selection: ReturnType<typeof useCubeSelection>;
}

/**
 * The locker-bank selection in a dialog (full-screen on phones), so the booking view underneath keeps its context
 * instead of being swapped out. Shared by the standard and simplified booking views.
 */
export function CubeSelectionDialog({bookedTypes, selection}: CubeSelectionDialogProps) {
	const fullScreen = useMobileFullScreen();
	useCloseOnBack(selection.selecting, selection.cancelCubeSelection);
	return (
		<Dialog
			fullScreen={fullScreen}
			fullWidth
			maxWidth="sm"
			open={selection.selecting}
			onClose={selection.cancelCubeSelection}
		>
			<DialogContent sx={{display: "flex", flexDirection: "column", justifyContent: "center"}}>
				<CubeSelection
					bookedTypes={bookedTypes}
					cubes={selection.cubeAvailabilities}
					triedCubeIds={selection.triedCubeIds}
					onSelect={selection.selectCube}
					onCancel={selection.cancelCubeSelection}
				/>
				{selection.occupyCubeError && <ErrorAlert error={selection.occupyCubeError} />}
			</DialogContent>
		</Dialog>
	);
}

interface LocationVerifierProps {
	requiredCubeId?: string;
	cubeDescription?: string;
	onVerified: (cubeId: string) => void;
	onCancel: () => void;
}

/**
 * Location verification in a dialog (full-screen on phones). The verifier is only mounted while open, so scanner and
 * typed-code state start fresh on every verification attempt — same semantics as the previous inline takeover.
 */
function LocationVerifierDialog({open, ...props}: LocationVerifierProps & { open: boolean }) {
	const fullScreen = useMobileFullScreen();
	useCloseOnBack(open, props.onCancel);
	return (
		<Dialog fullScreen={fullScreen} fullWidth maxWidth="sm" open={open} onClose={props.onCancel}>
			<DialogContent sx={{display: "flex", flexDirection: "column", justifyContent: "center"}}>
				{open && <LocationVerifier {...props} />}
			</DialogContent>
		</Dialog>
	);
}

function LocationVerifier({requiredCubeId, cubeDescription, onVerified, onCancel}: LocationVerifierProps) {
	const {booking} = useBooking();
	return (
		<LocationVerification
			bookingId={booking?.id ?? ""}
			onCancel={onCancel}
			onVerified={onVerified}
			qrcodeReaderEnabled={booking?.site.openingPrecaution === "QrCodeVerification"}
			requiredCubeId={requiredCubeId}
			cubeDescription={cubeDescription}
		/>
	);
}
